// Sticky "jump to subscription options" pill shown in the cart drawer.
//
// Loaded globally (see snippets/header-actions.liquid) so the custom element
// is defined once on page load. It must NOT be defined from an inline
// <script> inside snippets/cart-delivery-method-pill.liquid: that snippet
// only renders when the cart is non-empty, and morphed-in <script> nodes
// never execute — so on an empty-cart page load the element would stay
// undefined and never upgrade once add-to-cart morphs it into the drawer.
if (!customElements.get('cart-delivery-method-pill')) {
  class CartDeliveryMethodPill extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.cart-delivery-method-pill__button');
      this.scrollRoot = this.closest('.cart-drawer__content');
      this.dialogHost = this.closest('cart-drawer-component');
      if (!this.button || !this.scrollRoot) return;

      this.handleClick = this.handleClick.bind(this);
      this.handleIntersect = this.handleIntersect.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handleDialogOpen = this.handleDialogOpen.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);

      this.button.addEventListener('click', this.handleClick);
      document.addEventListener('cart:update', this.handleCartUpdate);
      this.dialogHost?.addEventListener('dialog:open', this.handleDialogOpen);
      this.dialogHost?.addEventListener('dialog:close', this.handleDialogClose);

      // Drawer already open at connect time (e.g. cart-drawer auto-open
      // during add-to-cart, or a morph during an open drawer). Otherwise
      // wait for dialog:open — observing a display:none subtree just
      // sets data-visible we'd have to clear again on open.
      if (this.dialogHost?.querySelector('dialog')?.open) this.attachObserver();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleClick);
      document.removeEventListener('cart:update', this.handleCartUpdate);
      this.dialogHost?.removeEventListener('dialog:open', this.handleDialogOpen);
      this.dialogHost?.removeEventListener('dialog:close', this.handleDialogClose);
      this.observer?.disconnect();
    }

    attachObserver() {
      this.observer?.disconnect();
      const target = this.scrollRoot?.querySelector('.cart-delivery-method');
      if (!target) return;
      this.observer = new IntersectionObserver(this.handleIntersect, {
        root: this.scrollRoot,
        threshold: 0,
      });
      this.observer.observe(target);
    }

    /** @param {IntersectionObserverEntry[]} entries */
    handleIntersect(entries) {
      const [entry] = entries;
      if (!entry) return;
      this.toggleAttribute('data-visible', !entry.isIntersecting);
    }

    handleCartUpdate() {
      // cart-items-component morphs the drawer body; the CDM node may have
      // been replaced, leaving the observer attached to a detached element.
      // Sever immediately, then defer re-attachment to next microtask so the
      // morph has settled before we re-query the new CDM target.
      this.observer?.disconnect();
      queueMicrotask(() => this.attachObserver());
    }

    handleDialogOpen() {
      // Reset per open: IntersectionObserver doesn't reliably refire on
      // display:none → block, so the previous session's data-visible can
      // linger. Clear it and re-observe against the now-laid-out drawer.
      this.removeAttribute('data-visible');
      this.attachObserver();
    }

    handleDialogClose() {
      // Drop observer + attribute so the next open starts hidden and the
      // first intersection callback decides afresh.
      this.observer?.disconnect();
      this.observer = null;
      this.removeAttribute('data-visible');
    }

    handleClick() {
      const target = this.scrollRoot?.querySelector('.cart-delivery-method');
      if (!target) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'start' });
    }
  }

  customElements.define('cart-delivery-method-pill', CartDeliveryMethodPill);
}
